import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import HHDialogTitle from 'components/common/HHDialogTitle'
import T from 'T'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import ActionsAutocompleteFormField from 'components/pricing/Services/edit-service/ActionsAutocompleteFormField'
import { selectServiceActions } from 'data/meta/metaSelectors'
import { useLazyFetchMetaFiltersQuery } from 'api/meta/getMetaFilters'
import { useForm } from 'react-hook-form'
import HHDialogActions from 'components/common/HHDialogActions'
import CancelButton from 'components/buttons/CancelButton'
import { noop } from 'lodash'
import SaveButton from 'components/buttons/SaveButton'
import { useLazyGetWorkOrderDetailsQuery, useUpdateWorkOrderActionMutation } from 'api/work-order/getWorkOrderDetails'
import { handleError } from 'utils/error'
import { toast } from 'react-toastify'

const EditActionDialog = ({ open = false, onClose = noop, workOrderId = '' }) => {
  const [fetchMetaFilters, { data: metaFiltersData }] = useLazyFetchMetaFiltersQuery()
  const [updateWorkOrderAction, { isLoading: isUpdatingAction }] = useUpdateWorkOrderActionMutation()
  const serviceActions = useMemo(() => selectServiceActions(metaFiltersData), [metaFiltersData])
  const [getWorkOrderDetails, { data }] = useLazyGetWorkOrderDetailsQuery()
  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      action: '',
    },
  })

  const onSave = async formData => {
    const { action } = formData
    const { actionName } = action
    try {
      await updateWorkOrderAction({
        workOrderId,
        action: actionName,
      }).unwrap()
      toast.success(T.ACTION_UPDATED_SUCCESSFULLY)
    } catch (e) {
      handleError(e)
    } finally {
      onClose()
    }
  }

  useEffect(() => {
    if (open) {
      fetchMetaFilters({})
    }
  }, [open])

  useEffect(() => {
    if (open && workOrderId) {
      getWorkOrderDetails({
        workOrderId,
      })
    }
  }, [workOrderId, open])

  useEffect(() => {
    if (data && serviceActions && open) {
      const { serviceAction } = data || {}
      if (!serviceAction) {
        return
      }

      const selectedAction = serviceActions.find(action => action.actionName === serviceAction)
      if (!selectedAction) {
        return
      }

      reset({
        action: selectedAction,
      })
    }
  }, [data, serviceActions, open])

  return (
    <Dialog open={open} maxWidth="sm" fullWidth onClose={onClose}>
      <HHDialogTitle title={T.EDIT_ACTION} onClose={onClose} />
      <DialogContent>
        <ActionsAutocompleteFormField
          sx={{ mt: 1 }}
          rules={{ required: T.REQUIRED_FIELD_MSG }}
          name="action"
          control={control}
          options={serviceActions}
          fullWidth
        />
      </DialogContent>
      <HHDialogActions>
        <CancelButton onClick={onClose} />
        <SaveButton isLoading={isUpdatingAction} disabled={isUpdatingAction} onClick={handleSubmit(onSave)} />
      </HHDialogActions>
    </Dialog>
  )
}

EditActionDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  workOrderId: PropTypes.string,
}

export default EditActionDialog
